import type { FilterGroup } from 'types/userConfig/userConfig';
import { generateUniqueId } from 'utils/helpers';
import { createDynamicValue } from 'utils/resolve-dynamic-values';

/**
 * תיקיות סרגל-הצד של השיחות + שיוך תצוגות (custom views) לתיקיות.
 *
 * ⚠️ אחסון מקומי בלבד (localStorage): לשרת אין כרגע תמיכה בשמירת תיקיות או בשיוך
 * תצוגה→תיקייה. הגדרות התצוגות עצמן (userConfig.customViews) ממשיכות להישמר בשרת,
 * אבל מבנה התיקיות והשיוך נשמרים מקומית עד שתתווסף תמיכת DB.
 *
 * TODO(DB): להעביר את `customFolders` ואת `viewFolderMap` לשמירה בשרת
 * (כנראה כחלק מ-UserConfig / טבלה ייעודית) כדי שיסונכרנו בין מכשירים ומשתמשים.
 */

/** מזהי התיקיות המובנות — תואמים ל-ChatSidebarScopeTab */
export type BuiltinFolderId = 'mine' | 'team' | 'all';

/** תיקיית מערכת «לא משויכות» — תור שיוך כללי (לא mine/team) */
export const BUILTIN_UNASSIGNED_FOLDER_ID = 'builtin-unassigned';

/**
 * תיקיית מערכת «מותאמות» — בית לכל תצוגה מותאמת שלא שויכה לתיקייה אצל המשתמש.
 * שיוך תצוגה→תיקייה (viewFolderMap) הוא פר-משתמש, ולכן תצוגות חברה שאחרים יצרו
 * מגיעות תמיד "יתומות"; עד 26.10 הן נפלו לתיקיית «שלי» והוצגו שם כפריט זר.
 */
export const BUILTIN_CUSTOM_VIEWS_FOLDER_ID = 'builtin-custom-views';

/** תיקייה שהמשתמש יצר */
export type ChatSidebarFolder = {
  id: string;
  name: string;
};

/** מזהה תיקיית "ללא שיוך" — וירטואלית, מציגה תצוגות שעדיין לא שויכו לתיקייה */
export const UNCATEGORIZED_FOLDER_ID = 'uncategorized';

/** מיפוי: מזהה תצוגה (custom view) → מזהה תיקייה (מובנה / מותאם) */
export type ViewFolderMap = Record<string, string>;

const FOLDERS_KEY = 'funner.chatSidebar.customFolders.v1';
const VIEW_FOLDER_MAP_KEY = 'funner.chatSidebar.viewFolderMap.v1';

export function loadCustomFolders(): ChatSidebarFolder[] {
  try {
    const raw = localStorage.getItem(FOLDERS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((f): f is ChatSidebarFolder => !!f && typeof f.id === 'string' && typeof f.name === 'string')
      .map((f) => ({ id: f.id, name: f.name }));
  } catch {
    return [];
  }
}

export function saveCustomFolders(folders: ChatSidebarFolder[]): void {
  try {
    localStorage.setItem(FOLDERS_KEY, JSON.stringify(folders));
  } catch {
    /* ignore */
  }
}

export function loadViewFolderMap(): ViewFolderMap {
  try {
    const raw = localStorage.getItem(VIEW_FOLDER_MAP_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: ViewFolderMap = {};
    for (const [viewId, folderId] of Object.entries(parsed)) {
      if (typeof folderId === 'string') out[viewId] = folderId;
    }
    return out;
  } catch {
    return {};
  }
}

export function saveViewFolderMap(map: ViewFolderMap): void {
  try {
    localStorage.setItem(VIEW_FOLDER_MAP_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

/** מזהה ייחודי לתיקייה חדשה */
export function generateFolderId(): string {
  return `folder-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

/** פילטר קבוע ליצירת תצוגה בתיקיית «שלי» או «לא משויכות» (UI בלבד — אין שינוי לוגיקת API) */
export function getLockedFolderFilterPreset(folderId?: string): FilterGroup[] | null {
  if (folderId === 'mine') {
    const groupId = generateUniqueId([], 5);
    return [
      {
        id: groupId,
        logic: 'and',
        conditions: [
          {
            id: generateUniqueId([], 5),
            field: 'lastSession.manager.id',
            operator: 'eq',
            value: createDynamicValue('current_user'),
          },
        ],
      },
    ];
  }
  if (folderId === BUILTIN_UNASSIGNED_FOLDER_ID) {
    const groupId = generateUniqueId([], 5);
    return [
      {
        id: groupId,
        logic: 'and',
        conditions: [
          {
            id: generateUniqueId([], 5),
            field: 'lastSession.manager.id',
            operator: 'isnull',
          },
        ],
      },
    ];
  }
  return null;
}

export function isLockedFolderFilterPreset(folderId?: string): boolean {
  return getLockedFolderFilterPreset(folderId) !== null;
}
